import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { Logo } from '../components/ui/Logo';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { LanguageSwitcher } from '../components/ui/LanguageSwitcher';
import { useMarketplace } from '../contexts/MarketplaceContext';
import { useLanguage } from '../contexts/LanguageContext';
import type { TranslationKey } from '../data/translations';
import { images } from '../data/images';
import { rise, spring, stagger } from '../utils/motion';
import type { Role } from '../types/marketplace';
const options: {
  role: Role;
  title: TranslationKey;
  body: TranslationKey;
  image: string;
  to: string;
}[] = [{
  role: 'performer',
  title: 'cta.perform',
  body: 'role.perform.body',
  image: images.ploy,
  to: '/onboarding/performer'
}, {
  role: 'organiser',
  title: 'cta.organise',
  body: 'role.organise.body',
  image: images.rooftop,
  to: '/onboarding/organiser'
}];
export function RoleChoice() {
  const navigate = useNavigate();
  const {
    setRole,
    role
  } = useMarketplace();
  const {
    t
  } = useLanguage();
  return <div className="flex min-h-[100dvh] w-full flex-col bg-bg text-ink" data-id="element-1377" data-mp="3clfl88upan0v">
      <header className="mx-auto flex w-full max-w-5xl items-center justify-between px-5 py-5" data-id="element-1378" data-mp="37k8hckvoi7zh">
        <Link to="/" aria-label="Encorza home" data-id="element-1379" data-mp="32mq44iamp6lo">
          <Logo data-id="element-1380" data-mp="43rkkhwx9ngs" />
        </Link>
        <div className="flex items-center gap-2" data-id="element-1381" data-mp="41ydx2mb738s">
          <LanguageSwitcher id="role" data-id="element-1382" data-mp="3v9kkb30exjtc" />
          <ThemeToggle data-id="element-1383" data-mp="3valznjogosgv" />
        </div>
      </header>
      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center px-5 pb-16" data-id="element-1384" data-mp="3qiui1yna9x2v">
        <motion.div variants={stagger} initial="hidden" animate="show" data-id="element-1385" data-mp="3qghtv3qxmqhd">
          <motion.h1 variants={rise} className="text-[36px] font-bold leading-[1.1] tracking-[-0.015em] lg:text-[56px]" data-id="element-1386" data-mp="3ljhkaiuvj5li">
            {t('role.title')}
          </motion.h1>
          <motion.p variants={rise} className="mt-3 text-[17px] text-ink-2" data-id="element-1387" data-mp="i1znd6gn3izv">
            {t('role.subtitle')}
          </motion.p>
          <div className="mt-10 grid gap-4 md:grid-cols-2" data-id="element-1388" data-mp="2cboabj14cv0i">
            {options.map(o => <motion.button key={o.role} variants={rise} type="button" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} transition={spring.snappy} onClick={() => {
            setRole(o.role);
            navigate(o.to);
          }} className="group relative aspect-[4/3] overflow-hidden rounded-lg text-left focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand md:aspect-[4/5]" data-id="element-1389" data-mp="2gbop54s4r883">
                <img src={o.image} alt="" className="absolute inset-0 h-full w-full object-cover" data-id="element-1390" data-mp="1ombxm96l24tg" />
                <span className="absolute inset-x-3 bottom-3 flex items-center gap-4 rounded-md bg-surface/95 p-5 backdrop-blur-md" data-id="element-1391" data-mp="3e1jpzll9k12v">
                  <span className="min-w-0 flex-1" data-id="element-1392" data-mp="3ix8r3tcxlq1a">
                    <span className="block text-2xl font-bold tracking-[-0.01em] text-ink" data-id="element-1393" data-mp="3t2gpavuzz8df">{t(o.title)}</span>
                    <span className="mt-1 block text-[15px] text-ink-2" data-id="element-1394" data-mp="3o5glkrlmehph">{t(o.body)}</span>
                  </span>
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-accent text-on-accent transition-transform duration-150 group-hover:translate-x-1" data-id="element-1395" data-mp="5rqtjjufw99s">
                    <ArrowRightIcon className="h-5 w-5" data-id="element-1396" data-mp="fnaqm9kqpe04" />
                  </span>
                </span>
              </motion.button>)}
          </div>
          <p className="mt-8 text-center text-[15px] text-ink-2" data-id="element-1397" data-mp="op1x3gn3tov1">
            {t('role.already')}{' '}
            <Link to={role === 'organiser' ? '/o/events' : '/p/discover'} className="font-bold text-ink underline-offset-4 hover:underline" data-id="element-1398" data-mp="knq18tdn07ee">
              {t('nav.signIn')}
            </Link>
          </p>
        </motion.div>
      </main>
    </div>;
}